import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { Fir8990ScheduleBService } from './fir-8990-schedule-b.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-fir-8990-schedule-b',
    templateUrl: './fir-8990-schedule-b.component.html',
    styleUrls: ['./fir-8990-schedule-b.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class Fir8990ScheduleBComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private subscriptions = new Subscription();
  private isInitialized = false;

  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  colData: any = {};

  minimise = false;
  crudLoading = false;
  showFormLoader = false;
  modalTitle = 'Form 8990 Schedule B';

  amountValue = '';

  @Input('col-data')
  set _colData(colData: any) {
    try {
      const c = typeof colData === 'string' ? safeJsonInput<any>(colData || '{}', null, 'fir-8990-schedule-b.parse1') : extractObject(colData);
      this.colData = c;
      this.resetAndTryInit();
    } catch (e) {
      console.log('Col data not yet stable.');
    }
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private scheduleB8990Service: Fir8990ScheduleBService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.resetAndTryInit();
    this.subscriptions.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.resetAndTryInit();
      })
    );
    this.subscriptions.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.resetAndTryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydratedGp = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydratedGp);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydratedGp) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    const rowObj = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
    this.rowData = rowObj;
    this.cdr.markForCheck();
  }

  private resetAndTryInit(): void {
    this.isInitialized = false;
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    const baseOk = !!(this.GENERAL_CONFIG?.base_url || this.baseURLs?.api);
    if (
      !baseOk ||
      !this.globalService?.globalParams ||
      !this.rowData ||
      Object.keys(this.rowData).length === 0
    ) {
      this.cdr.markForCheck();
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    const map = this.colData?.map;
    if (map && this.rowData[map] !== undefined) {
      this.amountValue = this.rowData[map] != null ? String(this.rowData[map]) : '';
    }
    this.cdr.detectChanges();
  }

  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save', 'gtw-snackbar--yellow');
      return;
    }
    const map = this.colData?.map;
    const acctKeyProp = map ? map + '_ACCT_KEY' : '';
    const acct_key = this.rowData[acctKeyProp] ?? '';
    const combination_key = this.rowData.COMBINATION_KEY ?? this.rowData.HO_COMBINATION_KEY ?? '';

    this.crudLoading = true;
    this.cdr.detectChanges();

    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    this.scheduleB8990Service
      .save8990ScheduleBAmounts(
        baseUrl,
        { combination_key, acct_key, amount: this.amountValue },
        this.globalService,
        this.filterParams,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          this.crudLoading = false;
          this.cdr.detectChanges();
          if (data?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Data saved successfully', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          } else {
            this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Save failed', 'gtw-snackbar--red');
          }
        },
        () => {
          this.crudLoading = false;
          this.cdr.detectChanges();
          this.gtwSnackbarService.openSnackBar('Error saving. Please try again.', 'gtw-snackbar--red');
        }
      );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
